import { gql } from '@apollo/client'
import { gqlSanitize } from '@fs/zion-rootstech'
import { brightspotConfig } from '../../config'
import { ExhibitorDetail, MinimumExhibitorDetail } from './fragments/ExhibitorDetail'
import Pagination from './fragments/Pagination'

const { query, imageSizes } = brightspotConfig
const { defaults } = query
const { promoImage, expoThumbnailImage, graphicsImage, thumbnailImage, logoImage, profileImage } = imageSizes

export function getExhibitorPreviewDetail() {
  return gql(`
    ${ExhibitorDetail}
    ${Pagination}
    query ExhibitorSearch(
      $ids: [String],
      $associatedUser: String
      $pageRequest: PaginationInput = ${gqlSanitize(defaults.pageRequest)},
      $visibilityRequest: VisibilityInput = ${gqlSanitize(defaults.visibilityRequest)},
      $profileImage_crop: Boolean = false,
      $profileImage_height: Int = ${defaults.imageSize},
      $profileImage_width: Int = ${defaults.imageSize},
      $promoImage_crop: Boolean = false,
      $promoImage_height: Int = ${defaults.imageSize},
      $promoImage_width: Int = ${defaults.imageSize},
      $thumbnailImage_crop: Boolean = false,
      $thumbnailImage_height: Int = ${defaults.imageSize},
      $thumbnailImage_width: Int = ${defaults.imageSize},
      $expoThumbnailImage_crop: Boolean = false,
      $expoThumbnailImage_height: Int = ${defaults.imageSize},
      $expoThumbnailImage_width: Int = ${defaults.imageSize},
      $graphicsImage_crop: Boolean = false,
      $graphicsImage_height: Int = ${defaults.imageSize},
      $graphicsImage_width: Int = ${defaults.imageSize},
      $logoImage_crop: Boolean = false,
      $logoImage_height: Int = ${defaults.imageSize},
      $logoImage_width: Int = ${defaults.imageSize}) {
      ExhibitorSearch(
        ids: $ids, 
        associatedUsers: $associatedUser, 
        pageRequest: $pageRequest,
        visibilityRequest: $visibilityRequest
        ) {
          paginationData {
            ...Pagination
          }
          results {
            ...ExhibitorDetail
          }
      }
    }
  `)
}

export function getExhibitorMutation() {
  return gql(`
  ${ExhibitorDetail}
  mutation GetExhibitorMutation(
    $target: MutationTargetInput!,
    $profileImage_crop: Boolean = false,
    $profileImage_height: Int = ${defaults.imageSize},
    $profileImage_width: Int = ${defaults.imageSize},
    $promoImage_crop: Boolean = false,
    $promoImage_height: Int = ${defaults.imageSize},
    $promoImage_width: Int = ${defaults.imageSize},
    $thumbnailImage_crop: Boolean = false,
    $thumbnailImage_height: Int = ${defaults.imageSize},
    $thumbnailImage_width: Int = ${defaults.imageSize},
    $expoThumbnailImage_crop: Boolean = false,
    $expoThumbnailImage_height: Int = ${defaults.imageSize},
    $expoThumbnailImage_width: Int = ${defaults.imageSize},
    $graphicsImage_crop: Boolean = false,
    $graphicsImage_height: Int = ${defaults.imageSize},
    $graphicsImage_width: Int = ${defaults.imageSize},
    $logoImage_crop: Boolean = false,
    $logoImage_height: Int = ${defaults.imageSize},
    $logoImage_width: Int = ${defaults.imageSize}) {
    ExhibitorMutation(_target: $target) {
      result {
        ...ExhibitorDetail
      }
    }
  }`)
}

export async function getExhibitorByIdAndLocale(client, { id, locale }) {
  const variables = {
    target: { id, locale },
    ...promoImage,
    ...thumbnailImage,
    ...logoImage,
    ...expoThumbnailImage,
    ...graphicsImage,
    ...profileImage,
  }

  // NOTE: this is a mutation so we can query the exhibitor by locale
  const response = await client.mutate({ mutation: getExhibitorMutation(), variables })
  return response?.data?.ExhibitorMutation?.result
}

export function queryExhibitorNames() {
  return gql(`
  ${MinimumExhibitorDetail}
  ${Pagination}
  query ExhibitorSearch(
    $visibilityRequest: VisibilityInput = ${gqlSanitize(defaults.visibilityRequest)},
    $pageRequest: PaginationInput = ${gqlSanitize(defaults.pageRequest)},
    $logoImage_crop: Boolean = false,
    $logoImage_height: Int = ${defaults.imageSize},
    $logoImage_width: Int = ${defaults.imageSize}) {
    ExhibitorSearch(
      visibilityRequest: $visibilityRequest, 
      pageRequest: $pageRequest) {
        paginationData {
          ...Pagination
        }
        results {
          ...MinimumExhibitorDetail
        }
        __typename
    }
  }`)
}

export const vendorSearch = () => {
  return gql(`
    ${MinimumExhibitorDetail}
    ${Pagination}
    query ExhibitorSearch(
      $ids: [String],
      $logoImage_crop: Boolean = false,
      $logoImage_height: Int = ${defaults.imageSize},
      $logoImage_width: Int = ${defaults.imageSize},
      $pageRequest: PaginationInput = ${gqlSanitize(defaults.pageRequest)},
      $visibilityRequest: VisibilityInput = ${gqlSanitize(defaults.visibilityRequest)}) {
      ExhibitorSearch(
        ids: $ids, 
        visibilityRequest: $visibilityRequest,
        pageRequest: $pageRequest) {
          paginationData {
            ...Pagination
          }
          results {
            ...MinimumExhibitorDetail
          }
      }
    }
  `)
}

export function mutateExhibitor() {
  return gql(`
    ${ExhibitorDetail}
    mutation ExhibitorMutation(
      $target: MutationTargetInput,
      $workflow: WorkflowInput,
      $associatedUsers: String,
      $companyContactEmail: String,
      $companyContactUsPage: String,
      $companyDescription: String,
      $companyFaqs: String,
      $companySupportPage: String,
      $companyWebsite: String,
      $contentLocale: String,
      $downloadableResources: [ID],
      $exhibitorCategories: [ID],
      $graphics: [ID],
      $highResolutionLogo: ID,
      $logo: ID,
      $name: String,
      $promotions: [PromoInput],
      $promotionsBehavior: String,
      $sponsor: Boolean,
      $sponsorLevel: String,
      $socialAccounts: [CreatorSocialInput],
      $welcomeVideo: ID,
      $whiteOnTransparentLogo: ID,
      $supportingChat: Boolean,
      $profileImage_crop: Boolean = false,
      $profileImage_height: Int = ${defaults.imageSize},
      $profileImage_width: Int = ${defaults.imageSize},
      $promoImage_crop: Boolean = false,
      $promoImage_height: Int = ${defaults.imageSize},
      $promoImage_width: Int = ${defaults.imageSize},
      $thumbnailImage_crop: Boolean = false,
      $thumbnailImage_height: Int = ${defaults.imageSize},
      $thumbnailImage_width: Int = ${defaults.imageSize},
      $expoThumbnailImage_crop: Boolean = false,
      $expoThumbnailImage_height: Int = ${defaults.imageSize},
      $expoThumbnailImage_width: Int = ${defaults.imageSize},
      $graphicsImage_crop: Boolean = false,
      $graphicsImage_height: Int = ${defaults.imageSize},
      $graphicsImage_width: Int = ${defaults.imageSize},
      $logoImage_crop: Boolean = false,
      $logoImage_height: Int = ${defaults.imageSize},
      $logoImage_width: Int = ${defaults.imageSize}) {
      ExhibitorMutation(
        _target: $target,
        _workflow: $workflow,
        associatedUsers: $associatedUsers,
        companyContactEmail: $companyContactEmail,
        companyContactUsPage: $companyContactUsPage,
        companyDescription: $companyDescription,
        companyFaqs: $companyFaqs,
        companySupportPage: $companySupportPage,
        companyWebsite: $companyWebsite,
        contentLocale: $contentLocale,
        downloadableResources: $downloadableResources,
        exhibitorCategories: $exhibitorCategories,
        graphics: $graphics,
        highResolutionLogo: $highResolutionLogo,
        logo: $logo,
        name: $name,
        promotions: $promotions,
        promotionsBehavior: $promotionsBehavior,
        socialAccounts: $socialAccounts,
        sponsor: $sponsor,
        sponsorLevel: $sponsorLevel,
        welcomeVideo: $welcomeVideo,
        whiteOnTransparentLogo: $whiteOnTransparentLogo,
        supportingChat: $supportingChat) {
        result {
          ...ExhibitorDetail
        }
      }
    }
  `)
}

export async function createExhibitor(client, exhibitor) {
  const variables = {
    ...promoImage,
    ...thumbnailImage,
    ...logoImage,
    ...expoThumbnailImage,
    ...graphicsImage,
    ...profileImage,
  }

  // All these if statements allow us to use the same mutation but only change the values we need to. Otherwise, we could unintentionally remove values.
  if (exhibitor?.companyDescription) {
    variables.companyDescription = exhibitor.companyDescription
  }
  if (exhibitor?.companyWebsite !== undefined) {
    variables.companyWebsite = exhibitor.companyWebsite
  }
  if (exhibitor?.logo !== undefined) {
    variables.logo = exhibitor.logo
  }
  if (exhibitor?.name) {
    variables.name = exhibitor.name
  }
  if (exhibitor?.supportingChat !== undefined) {
    variables.supportingChat = exhibitor.supportingChat
  }
  if (exhibitor?.socialAccounts) {
    variables.socialAccounts = exhibitor.socialAccounts.map((socialAccount) => ({
      platform: socialAccount.platform,
      urlOrHandle: socialAccount.urlOrHandle ? socialAccount.urlOrHandle : '',
    }))
  }
  if (exhibitor?.exhibitorAssociatedUsers) {
    variables.associatedUsers = exhibitor?.exhibitorAssociatedUsers
  }
  if (exhibitor.sponsor !== null) {
    variables.sponsor = exhibitor.sponsor
  }
  if (exhibitor.sponsorLevel !== null) {
    variables.sponsorLevel = exhibitor.sponsorLevel
  }
  if (exhibitor?.transition) {
    variables.workflow = { action: 'TRANSITION', targetState: { next: true } }
  }
  if (exhibitor?.companyContactEmail) {
    variables.companyContactEmail = exhibitor.companyContactEmail
  }
  if (exhibitor?.companyContactUsPage !== undefined) {
    variables.companyContactUsPage = exhibitor.companyContactUsPage
  }
  if (exhibitor?.companyFaqs !== undefined) {
    variables.companyFaqs = exhibitor.companyFaqs
  }
  if (exhibitor?.companySupportPage !== undefined) {
    variables.companySupportPage = exhibitor.companySupportPage
  }
  if (exhibitor?.downloadableResources) {
    variables.downloadableResources = exhibitor.downloadableResources
  }
  if (exhibitor?.exhibitorCategories) {
    variables.exhibitorCategories = exhibitor.exhibitorCategories
  }
  if (exhibitor?.graphics !== undefined) {
    variables.graphics = exhibitor.graphics
  }
  if (exhibitor?.welcomeVideo !== undefined) {
    variables.welcomeVideo = exhibitor.welcomeVideo
  }
  if (exhibitor?.highResolutionLogo !== undefined) {
    variables.highResolutionLogo = exhibitor.highResolutionLogo
  }
  if (exhibitor?.whiteOnTransparentLogo !== undefined) {
    variables.whiteOnTransparentLogo = exhibitor.whiteOnTransparentLogo
  }

  // If we have an exhibitor id we are doing an update.  Otherwise, it is a create
  if (exhibitor?.id) {
    variables.target = { id: exhibitor.id, locale: exhibitor.locale }
  } else {
    variables.contentLocale = exhibitor?.locale ? exhibitor.locale : ''
  }

  if (exhibitor?.promotions !== undefined) {
    const mappedPromos =
      exhibitor?.promotions.length === 0
        ? []
        : exhibitor.promotions.map((promo) => {
            return {
              _id: promo?.id || promo?.promoId || '',
              description: promo?.description,
              title: promo?.title,
              item: { type: 'EXTERNAL', external: { url: promo?.url } },
              image: promo?.thumbnail || promo?.image?.id || '',
            }
          })
    if (exhibitor?.promotionsBehavior) {
      variables.promotionsBehavior = exhibitor?.promotionsBehavior
    }
    variables.promotions = mappedPromos
  }

  const response = await client.mutate({ mutation: mutateExhibitor(), variables })
  return response?.data?.ExhibitorMutation?.result
}

export async function updateExhibitor(client, exhibitor) {
  return createExhibitor(client, exhibitor)
}
